// Viewport-keyed data fetching for the map. Bounds are rounded so panning a
// few pixels reuses the same request key; each layer paginates independently
// and renders its pages as they land (see usePaginatedLayer).
//
// Filtering is now entirely server-side: pdbq ≥ 0.8.0 exposes PostGIS
// predicates through `GeometryFilterOps`, so the bbox, place types, elevation
// range, activities and date range all go into the GraphQL filter argument
// instead of being sieved here after the fact.
import {
  MAP_EVENTS_PAGE,
  MAP_OUTINGS_PAGE,
  MAP_PLACES_PAGE,
  MAP_REGIONS_PAGE,
  MAP_ROUTES_PAGE,
  type Connection,
  type EventPin,
  type MapEventsPageResult,
  type MapOutingsPageResult,
  type MapPlacesPageResult,
  type MapRegionsPageResult,
  type MapRoutesPageResult,
  type OutingPin,
  type PlacePin,
  type RegionShape,
  type RoutePin,
} from "../lib/queries";
import {
  toEventMapFilter,
  toOutingMapFilter,
  toPlaceFilter,
  toRegionFilter,
  toRouteFilter,
  type BBox,
  type FilterState,
} from "../lib/filters";
import { featureBudget, PINS_MIN_ZOOM, ROUTES_MIN_ZOOM, ROUTES_PAGE_SIZE, TRACKS_MIN_ZOOM } from "./zoomConfig";
import { usePaginatedLayer } from "./usePaginatedLayer";

export type Viewport = { minLon: number; minLat: number; maxLon: number; maxLat: number; zoom: number };

function roundedBbox(v: Viewport): BBox {
  // Snap the box outward onto a zoom-dependent grid rather than padding by a
  // percentage: the key only changes when a pan crosses a cell boundary, so
  // the cache stays just as stable while the overdraw stays bounded by one
  // cell instead of 40% of the viewport. A small pad keeps features whose
  // geometry starts just off-screen from popping in at the edge.
  const cell = 360 / Math.pow(2, Math.floor(v.zoom) + 3);
  const padLon = (v.maxLon - v.minLon) * 0.05;
  const padLat = (v.maxLat - v.minLat) * 0.05;
  const lo = (n: number) => Math.floor(n / cell) * cell;
  const hi = (n: number) => Math.ceil(n / cell) * cell;
  return {
    minX: lo(v.minLon - padLon),
    minY: lo(v.minLat - padLat),
    maxX: hi(v.maxLon + padLon),
    maxY: hi(v.maxLat + padLat),
  };
}

export function useViewportData(viewport: Viewport | null, filters: FilterState) {
  const bbox = viewport ? roundedBbox(viewport) : null;
  const zoom = viewport?.zoom ?? 0;
  const withRoutes = filters.showRoutes && zoom >= ROUTES_MIN_ZOOM;
  // Tracks are a route sub-layer: no routes, no tracks, whatever showTracks says.
  const withTracks = filters.showRoutes && filters.showTracks && zoom >= TRACKS_MIN_ZOOM;
  // Places/outings render as clusters below the route threshold, so they fetch
  // much earlier — but never below PINS_MIN_ZOOM, where the viewport is so
  // wide the budgeted sample would be an arbitrary slice of the world.
  const withPins = zoom >= PINS_MIN_ZOOM;

  // One key per layer: identical inputs reuse the in-flight fetch, any change
  // aborts it. Budgets are part of the key so a zoom that raises the ceiling
  // refetches to fill it.
  const placeBudget = featureBudget.places(zoom);
  const routeBudget = featureBudget.routes(zoom, withTracks);
  const regionBudget = featureBudget.regions(zoom);
  const outingBudget = featureBudget.outings(zoom);
  const eventBudget = featureBudget.events(zoom);

  // Rebuilt every render, which is fine: the layer hooks key off the
  // serialized filter, so an identical filter object never triggers a refetch.
  const placeFilter = bbox ? toPlaceFilter(filters, bbox) : null;
  const routeFilter = bbox ? toRouteFilter(filters, bbox) : null;
  const regionFilter = bbox ? toRegionFilter(filters, bbox) : null;
  const outingFilter = bbox ? toOutingMapFilter(filters, bbox) : null;
  const eventFilter = bbox ? toEventMapFilter(filters, bbox) : null;

  const places = usePaginatedLayer<PlacePin>({
    key: `places:${JSON.stringify(placeFilter)}:${placeBudget}`,
    enabled: !!bbox && withPins,
    query: MAP_PLACES_PAGE,
    variables: { filter: placeFilter },
    select: (d: never) => (d as MapPlacesPageResult).places as Connection<PlacePin>,
    budget: placeBudget,
  });

  const routes = usePaginatedLayer<RoutePin>({
    key: `routes:${JSON.stringify(routeFilter)}:${routeBudget}:${withTracks}`,
    enabled: !!bbox && withRoutes,
    query: MAP_ROUTES_PAGE,
    variables: { filter: routeFilter, withTracks },
    select: (d: never) => (d as MapRoutesPageResult).routes as Connection<RoutePin>,
    budget: routeBudget,
    pageSize: withTracks ? ROUTES_PAGE_SIZE.withTracks : ROUTES_PAGE_SIZE.withoutTracks,
  });

  const regions = usePaginatedLayer<RegionShape>({
    key: `regions:${JSON.stringify(regionFilter)}:${regionBudget}`,
    enabled: !!bbox && filters.showRegions,
    query: MAP_REGIONS_PAGE,
    variables: { filter: regionFilter },
    select: (d: never) => (d as MapRegionsPageResult).regions as Connection<RegionShape>,
    budget: regionBudget,
  });

  const outings = usePaginatedLayer<OutingPin>({
    key: `outings:${JSON.stringify(outingFilter)}:${outingBudget}`,
    enabled: !!bbox && filters.showOutings && withPins,
    query: MAP_OUTINGS_PAGE,
    variables: { filter: outingFilter },
    select: (d: never) => (d as MapOutingsPageResult).outings as Connection<OutingPin>,
    budget: outingBudget,
  });

  const events = usePaginatedLayer<EventPin>({
    key: `events:${JSON.stringify(eventFilter)}:${eventBudget}`,
    enabled: !!bbox && filters.showEvents && withPins,
    query: MAP_EVENTS_PAGE,
    variables: { filter: eventFilter },
    select: (d: never) => (d as MapEventsPageResult).events as Connection<EventPin>,
    budget: eventBudget,
  });

  return {
    places: places.nodes,
    routes: routes.nodes,
    regions: regions.nodes,
    outings: outings.nodes,
    events: events.nodes,
    withRoutes,
    withTracks,
    withPins,
    isFetching: places.isFetching || routes.isFetching || regions.isFetching || outings.isFetching || events.isFetching,
    error: places.error ?? routes.error ?? regions.error ?? outings.error ?? events.error,
    /** Server-side totals and whether a budget cut the layer short. */
    counts: {
      places: { shown: places.nodes.length, total: places.totalCount, truncated: places.truncated },
      routes: { shown: routes.nodes.length, total: routes.totalCount, truncated: routes.truncated },
      regions: { shown: regions.nodes.length, total: regions.totalCount, truncated: regions.truncated },
      outings: { shown: outings.nodes.length, total: outings.totalCount, truncated: outings.truncated },
      events: { shown: events.nodes.length, total: events.totalCount, truncated: events.truncated },
    },
    truncated: places.truncated || routes.truncated || regions.truncated || outings.truncated || events.truncated,
  };
}
